import React, {
  FC,
  MouseEventHandler,
  ReactNode,
  useEffect,
  useState,
  useRef,
} from "react";
import { useLocation, Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IconDefinition } from "@fortawesome/free-regular-svg-icons";
import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
} from "@headlessui/react";
import { classNames } from "@/shared/utils/classnames";
import {
  ChevronRightIcon,
  Bars3Icon,
  XMarkIcon,
} from "@heroicons/react/20/solid";
import { Avatar } from "../atoms/Avatar";
import { motion, AnimatePresence } from "framer-motion";
import { useDispatch } from "react-redux";
import { toggleSidebar } from "@/app/branchs/branch.slice";
import { useSelector } from "react-redux";
import { RootState } from "@/app/app.store";

export type SideBarProps = {
  title?: string;
  subtitle?: string;
  logoUrl?: string;
  active?: boolean;
  top: ReactNode;
  bottom: ReactNode;
  items: Array<SideBarItemProps>;
};

export const SideBar: FC<SideBarProps> = ({
  title,
  subtitle,
  items,
  logoUrl,
  top,
  bottom,
}) => {
  const [isDesktopActive, setIsDesktopActive] = useState(false);
  const location = useLocation();
  const isMobileOpen = useSelector((state: RootState) => state.branch.sidebar);
  const dispatch = useDispatch();
  const sidebarRef = useRef<HTMLDivElement>(null); // Ref to track sidebar element

  useEffect(() => {
    setIsDesktopActive(isMobileOpen ? false : isDesktopActive);
  }, [isMobileOpen]);

  // Handle clicks outside the sidebar
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        sidebarRef.current &&
        !sidebarRef.current.contains(event.target as Node) &&
        isMobileOpen
      ) {
        dispatch(toggleSidebar(false));
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isMobileOpen, dispatch]);

  const handleDesktopHoverStart = () => {
    setIsDesktopActive(true);
  };

  const handleDesktopHoverEnd = () => {
    setIsDesktopActive(false);
  };

  const closeSideBar = () => {
    dispatch(toggleSidebar(false));
  };

  return (
    <>
      {/* Mobile Sidebar */}
      <AnimatePresence>
        {isMobileOpen && (
          <>
            {/* Overlay for clicking outside */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.5 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 bg-black z-30 lg:hidden"
            />
            <motion.aside
              ref={sidebarRef}
              initial={{ x: -300 }}
              animate={{ x: 0 }}
              exit={{ x: -300 }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
              className="lg:hidden fixed top-0 left-0 w-64 h-full bg-white shadow-xl z-40 overflow-y-auto sidebar-scrollbar"
            >
              <nav className="flex flex-col h-full pt-16 relative">
                {/* Close Button */}
                <button
                  onClick={closeSideBar}
                  className="absolute top-4 right-4 text-gray-600 hover:text-gray-800 lg:hidden"
                  aria-label="Close sidebar"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
                <div className="flex p-4 gap-3 items-center">
                  {logoUrl ? (
                    <Avatar
                      src={logoUrl}
                      style={{ width: 60, height: 60 }}
                      className="rounded-xl"
                    />
                  ) : (
                    <div className="bg-gray-300 animate-pulse w-16 h-16 rounded-xl" />
                  )}
                  <div className="flex flex-col justify-center">
                    <p className="font-bold text-lg">{title}</p>
                    <p className="text-sm text-gray-600">{subtitle}</p>
                  </div>
                </div>
                {top}
                <ul role="list" className="flex flex-1 flex-col gap-y-2 px-4">
                  <li>
                    <ul role="list" className="space-y-1">
                      {items.map((item) => {
                        const isActive =
                          location.pathname === item.href ||
                          location.pathname.startsWith(item.href);

                        return item.children ? (
                          <SideBarItemWithChildren
                            key={item.id}
                            {...item}
                            active={true}
                            isActive={isActive}
                          />
                        ) : (
                          <SideBarItem
                            key={item.id}
                            {...item}
                            active={true}
                            isActive={isActive}
                          />
                        );
                      })}
                    </ul>
                  </li>
                </ul>
                {bottom}
              </nav>
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* Desktop Sidebar */}
      <div
        className="hidden lg:block z-50"
        style={{
          width: 100,
          height: "100%",
          overflow: "visible",
          position: "relative",
        }}
      >
        <aside
          className={`bg-white border-left-solid border-2 border-grey-700 ${
            isDesktopActive ? "shadow-md" : ""
          }`}
          onMouseEnter={handleDesktopHoverStart}
          onMouseLeave={handleDesktopHoverEnd}
          style={{
            width: isDesktopActive ? 300 : 100,
            height: "100%",
            position: "absolute",
            zIndex: 2,
            transition: "width 0.3s ease",
          }}
        >
          <nav className="flex flex-1 flex-col h-full overflow-y-auto sidebar-scrollbar">
            {isDesktopActive ? (
              <div className="flex p-2 gap-2">
                {logoUrl ? (
                  <Avatar src={logoUrl} style={{ width: 60, height: 60 }} />
                ) : (
                  <div className="bg-gray-300 animate-pulse w-16 h-16 rounded-xl" />
                )}
                <div className="flex flex-col justify-center">
                  <p className="font-bold">{title}</p>
                  <p className="text-sm">{subtitle}</p>
                </div>
              </div>
            ) : (
              <div className="flex w-full justify-center pt-2 pb-2">
                {logoUrl ? (
                  <Avatar src={logoUrl} style={{ width: 60, height: 60 }} />
                ) : (
                  <div className="bg-gray-300 animate-pulse w-16 h-16 rounded-xl" />
                )}
              </div>
            )}
            {top}
            <ul role="list" className="flex flex-1 flex-col gap-y-7">
              <li>
                <ul role="list">
                  {items.map((item) => {
                    const isActive =
                      location.pathname === item.href ||
                      location.pathname.startsWith(item.href);

                    return item.children ? (
                      <SideBarItemWithChildren
                        key={item.id}
                        {...item}
                        active={isDesktopActive}
                        isActive={isActive}
                      />
                    ) : (
                      <SideBarItem
                        key={item.id}
                        {...item}
                        active={isDesktopActive}
                        isActive={isActive}
                      />
                    );
                  })}
                </ul>
              </li>
            </ul>
            {bottom}
          </nav>
        </aside>
      </div>
    </>
  );
};

export type SideBarItemWithChildrenProps = {
  icon: IconDefinition;
  name: string;
  onClick?: MouseEventHandler;
  children: Array<SideBarItemProps>;
  href?: string;
  current?: boolean;
  active: boolean;
  hide?: boolean;
  hidden?: boolean;
  isActive: boolean;
};

export const SideBarItemWithChildren: FC<SideBarItemWithChildrenProps> = ({
  name,
  icon,
  children,
  active,
  isActive,
  hide,
  onClick,
}) => {
  const location = useLocation();

  // If item hide is true, return null
  if (hide) {
    return null;
  }

  // Check if at least one child is visible
  const hasVisibleChildren = children.some((child) => !child.hide);

  // If item hide is false, but all children are hidden, return null
  if (!hasVisibleChildren) {
    return null;
  }

  // If we reach here it means:
  // - item hide is false
  // - at least one child has hide false
  // Therefore, we show the item

  return (
    <Disclosure as="div">
      <DisclosureButton
        as="button"
        onClick={onClick}
        className={classNames(
          `group flex w-full items-center p-2 text-left font-semibold leading-6 text-gray-700 gap-2 hover:bg-gray-50 hover:text-primary`,
          active
            ? "w-full items-center p-2 leading-6 text-sm"
            : "w-full flex-col items-center justify-center",
          isActive ? "bg-gray-200 text-primary" : ""
        )}
      >
        <FontAwesomeIcon
          icon={icon}
          className={`text-gray-400 group-hover:text-primary ${
            active ? "" : "text-2xl"
          }`}
        />
        <span style={active ? {} : { fontSize: "0.65rem" }}>{name}</span>
        {active && (
          <ChevronRightIcon
            aria-hidden="true"
            className="ml-auto h-5 w-5 shrink-0 text-gray-400 group-data-[open]:rotate-90 group-data-[open]:text-gray-500"
          />
        )}
      </DisclosureButton>

      {active && (
        <DisclosurePanel as="ul">
          {children
            .filter((child) => !child.hide)
            .map((child) => {
              const isActiveChild = location.pathname === child.href;

              return (
                <li key={child.id}>
                  {child.href ? (
                    <Link
                      to={child.href}
                      onClick={child.onClick}
                      className={classNames(
                        child.current
                          ? "bg-gray-50"
                          : "hover:bg-gray-50 hover:text-primary",
                        "block py-2 pl-9 pr-2 text-sm leading-6 text-gray-700 w-full text-left",
                        isActiveChild ? "bg-gray-200 text-primary" : ""
                      )}
                    >
                      {child.name}
                    </Link>
                  ) : (
                    <button
                      onClick={child.onClick}
                      className={classNames(
                        child.current
                          ? "bg-gray-50"
                          : "hover:bg-gray-50 hover:text-primary",
                        "block py-2 pl-9 pr-2 text-sm leading-6 text-gray-700 w-full text-left",
                        isActiveChild ? "bg-gray-200 text-primary" : ""
                      )}
                    >
                      {child.name}
                    </button>
                  )}
                </li>
              );
            })}
        </DisclosurePanel>
      )}
    </Disclosure>
  );
};

export type SideBarItemProps = {
  id: string;
  key?: string;
  name: string;
  href?: string;
  current?: boolean;
  icon?: IconDefinition;
  hide?: boolean;
  hidden?: boolean;
  children?: SideBarItemProps[];
  onClick?: () => void;
  active?: boolean;
  isActive?: boolean;
};

export const SideBarItem: FC<SideBarItemProps> = ({
  active,
  name,
  href,
  hide,
  icon,
  onClick,
  isActive,
}) => {
  if (hide) {
    return null;
  }

  return (
    <li
      key={name}
      onClick={onClick}
      className="flex bg-white"
      style={active ? {} : { width: "100%", height: 75 }}
    >
      <Link
        to={href}
        className={classNames(
          `flex font-semibold text-sm gap-2 text-gray-700 hover:bg-gray-50 hover:text-primary`,
          isActive ? "bg-gray-200 text-primary" : "",
          active
            ? "w-full items-center p-2 leading-6"
            : "w-full flex-col items-center justify-center"
        )}
        style={active ? { flexDirection: "row" } : { flexDirection: "column" }}
      >
        <FontAwesomeIcon
          icon={icon}
          className={`text-gray-400 group-hover:text-primary ${
            active ? "" : "text-2xl"
          }`}
        />
        <span style={active ? {} : { fontSize: "0.65rem" }}>{name}</span>
      </Link>
    </li>
  );
};
